import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog';

export interface DiscardGuardOptions {
  /** Unsaved input exists. With react-hook-form read `formState.isDirty` IN RENDER (RHF only subscribes to fields read while rendering; read first inside a handler it is always false and the prompt never opens). */
  dirty: boolean;
  /** A submit is in flight — closing is refused. */
  busy: boolean;
  /** Really close and reset local state. Called directly when pristine, or after the user confirms discarding. */
  onClose: () => void;
  /**
   * Copy override (#29) for a guard whose confirm does NOT close a panel — e.g. footer "Vazgeç"
   * leaving an edit mode inside a drawer that stays open. Omitted: `drawer.discard.*` (which says
   * "close"). `cancel` falls back to `drawer.discard.cancel`.
   */
  labels?: { title: string; body: string; cancel?: string; confirm: string };
}
export interface DiscardGuard {
  /** Every close path (✕, Esc, outside click, "Vazgeç") calls this. */
  requestClose: () => void;
  /** Render once, as a sibling of the `Sheet`. */
  discardDialog: ReactNode;
}

export function useDiscardGuard({
  dirty,
  busy,
  onClose,
  labels,
}: DiscardGuardOptions): DiscardGuard {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  function requestClose() {
    if (busy) {
      return;
    }
    if (dirty) {
      setOpen(true);
      return;
    }
    onClose();
  }
  const discardDialog = (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      title={labels?.title ?? t('drawer.discard.title')}
      body={labels?.body ?? t('drawer.discard.body')}
      cancelLabel={labels?.cancel ?? t('drawer.discard.cancel')}
      confirmLabel={labels?.confirm ?? t('drawer.discard.confirm')}
      onConfirm={() => {
        setOpen(false);
        onClose();
      }}
    />
  );
  return { requestClose, discardDialog };
}
